import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

import type { CreateUserSshKeyBody, UserSshKeyResponse } from '../lib/api-client';

export type AsyncStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * A user's ACTIVE SSH public keys (`/users/{id}/ssh_keys`) — their SFTP identity.
 * Keys are uploaded by the user, or by an admin holding manage_users / full_access
 * over the user's organization tree; the platform never generates them. A 403 means
 * the actor may not see this user's keys (`forbidden`).
 */
export function useSshKeys(userId: string | null, { enabled = true }: { enabled?: boolean } = {}) {
  const apiClient = useApiClient();
  const [keys, setKeys] = useState<UserSshKeyResponse[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [error, setError] = useState<string | undefined>();
  const [forbidden, setForbidden] = useState(false);

  const reload = useCallback(async () => {
    if (!userId) return;
    setStatus('loading');
    setError(undefined);
    try {
      const res = await apiClient.listUserSshKeys(userId);
      setKeys(res.data);
      setForbidden(false);
      setStatus('success');
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 403) setForbidden(true);
      setError(err instanceof Error ? err.message : 'Failed to load SSH keys');
      setStatus('error');
    }
  }, [apiClient, userId]);

  useEffect(() => {
    if (!enabled || !userId) return;
    void reload();
  }, [enabled, userId, reload]);

  const addKey = useCallback(
    async (body: CreateUserSshKeyBody): Promise<UserSshKeyResponse> => {
      if (!userId) throw new Error('No user selected');
      const created = await apiClient.addUserSshKey(userId, body);
      setKeys((prev) => [created, ...prev]);
      return created;
    },
    [apiClient, userId],
  );

  const revokeKey = useCallback(
    async (keyId: string): Promise<void> => {
      if (!userId) return;
      await apiClient.deleteUserSshKey(userId, keyId);
      // The list is active keys only — a revoked key disappears (its row stays server-side for audit).
      setKeys((prev) => prev.filter((k) => k.id !== keyId));
    },
    [apiClient, userId],
  );

  return { keys, status, error, forbidden, reload, addKey, revokeKey };
}

/**
 * Active-key counts for a set of users (the SFTP pane's Accounts tab): one request per
 * user, fetched once per id; a failure or 403 shows as `null` (unknown). `invalidate(id)`
 * forgets one user's count so it is fetched again after an add or a revoke.
 */
export function useSshKeyCounts(userIds: readonly string[]) {
  const apiClient = useApiClient();
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [version, setVersion] = useState(0);
  const requested = useRef(new Set<string>());

  useEffect(() => {
    const missing = userIds.filter((id) => !requested.current.has(id));
    if (missing.length === 0) return;
    for (const id of missing) requested.current.add(id);
    void Promise.all(
      missing.map(async (id) => {
        try {
          const res = await apiClient.listUserSshKeys(id);
          return [id, res.data.length] as const;
        } catch {
          return [id, null] as const;
        }
      }),
    ).then((entries) => {
      setCounts((prev) => {
        const next = { ...prev };
        for (const [id, n] of entries) next[id] = n;
        return next;
      });
    });
  }, [apiClient, userIds, version]);

  const invalidate = useCallback((id: string) => {
    requested.current.delete(id);
    setCounts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setVersion((v) => v + 1);
  }, []);

  return { counts, invalidate };
}
